import { inject, Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { Store } from '@ngxs/store';
import { WorkAuthenticationV2Service } from '@work-frontend-monorepo/work-rest-api';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import 'dayjs/locale/de.js';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isLeapYear from 'dayjs/plugin/isLeapYear';
import isoWeek from 'dayjs/plugin/isoWeek';
import { environment } from '@work-frontend-monorepo/work-environments';
import { ProfileState, ProfileStateSetProfile, ProfileStateSetUser } from '@work-frontend-monorepo/work-ui/state-management';
import { LoggerService } from '@work-frontend-monorepo/work-ui/services';
import { StateReset } from 'ngxs-reset-plugin';
import { Router } from '@angular/router';

@Injectable()
export class AppInitializer {
  private readonly workAuthenticationV2Service = inject(WorkAuthenticationV2Service);
  private readonly store = inject(Store);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);

  async init() {
    let theme = 'theme-test-mode';
    if (environment.name === 'production') {
      switch (environment.tenantCompanyCommonName) {
        case 'Axova':
          theme = 'theme-axova';
          break;
        case 'SFG':
          theme = 'theme-sfg';
          break;
        default:
          theme = 'theme-axova';
          break;
      }
    }
    document.querySelector('html')?.setAttribute(theme, '');

    try {
      dayjs.locale('de');
      dayjs.extend(isBetween);
      dayjs.extend(isSameOrAfter);
      dayjs.extend(isSameOrBefore);
      dayjs.extend(isLeapYear);
      dayjs.extend(isoWeek);
      /**
       * Try to load profile. If not possible / not logged in, go to login page, otherwise go to dashboard afterward.
       */
      try {
        if (this.store.selectSnapshot(ProfileState.profile)) {
          const profile = await lastValueFrom(this.workAuthenticationV2Service.authenticationV2ControllerProfile());
          this.store.dispatch(new ProfileStateSetProfile(profile));
          if (profile.isUserEntity) {
            this.store.dispatch(new ProfileStateSetUser((profile as any).meta));
          } else {
            try {
              await lastValueFrom(this.workAuthenticationV2Service.authenticationV2ControllerLogout({ body: {} }));
            } catch (logoutException) {
              // Nothing to do here.
            }
            this.store.dispatch(new StateReset(ProfileState));
            await this.router.navigateByUrl('/auth/login');
          }
        }
        this.translateService.setDefaultLang('de');
      } catch (profileException) {
        LoggerService.ERROR(this, 'profileException', profileException);
      }
    } catch (appInitializerException) {
      LoggerService.ERROR(this, 'appInitializerException', appInitializerException);
    }
  }
}
